import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { getMetaAttribution } from "../lib/metaAttribution";
import { CreditCard, Building2, Check, QrCode, Plus, Minus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCurrency } from "../context/CurrencyContext";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { prefixLocale } from "../lib/i18nRouter";
import Seo from "../components/Seo";
import { productSchema, breadcrumbSchema, videoObjectSchema } from "../lib/schema";
import MediaGallery from "../components/MediaGallery";
import VideoBadge from "../components/VideoBadge";
import GemologistAssistant from "../components/GemologistAssistant";
import ReviewsSection from "../components/ReviewsSection";
import PayPalCheckoutButton from "../components/PayPalCheckoutButton";
import MarketplaceCheckoutButtons from "../components/MarketplaceCheckoutButtons";
import QrisPaymentModal from "../components/QrisPaymentModal";
import ShippingPicker from "../components/ShippingPicker";
import TX from "../components/TX";
import { trackViewContent, trackInitiateCheckout } from "../lib/metaPixel";
import { trackTTViewContent, trackTTInitiateCheckout } from "../lib/tiktokPixel";
import { ga4ViewItem, ga4BeginCheckout } from "../lib/ga4";

// Build a short, human-readable label for a variation row.
const variationLabel = (v) => {
  const bits = [];
  if (v.size) bits.push(`Size ${v.size}`);
  if (v.color) bits.push(v.color);
  if (v.carat != null && v.carat !== "") bits.push(`${v.carat} ct`);
  if (v.mm) bits.push(v.mm);
  return bits.length ? bits.join(" · ") : "Standard";
};

const GemstoneDetail = () => {
  const { id } = useParams();
  const { format, currency, rates } = useCurrency();
  const { t, lang } = useLang();
  const { addItem, count: cartCount } = useCart();
  const navigate = useNavigate();
  const lp = (p) => prefixLocale(p, lang);
  const [gem, setGem] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busyStripe, setBusyStripe] = useState(false);
  const [bank, setBank] = useState({ open: false, name: "", email: "", phone: "", notes: "", result: null });
  const [qrisOpen, setQrisOpen] = useState(false);
  const [variationId, setVariationId] = useState(null);
  // Map of variation_id → desired quantity. Buyers can pick non-zero qty on
  // multiple rows; the "Add to Cart" CTA below picks up every entry > 0.
  // The legacy single-select `variationId` is kept so the inline "Buy Now"
  // and bank-transfer flows (which only support one variation today) still
  // work — see `checkVariationSelected` below.
  const [variationQty, setVariationQty] = useState({});
  // Shipping selection from <ShippingPicker> — updated as user changes country/method
  const [shipping, setShipping] = useState({ method: null, country: "", costUsd: 0 });
  // Global pricing policy (base currency, auto-convert toggle, PayPal toggle).
  // Loaded once from /api/pricing/config below; sane defaults until then.
  const [pricingConfig, setPricingConfig] = useState({
    base_currency: "IDR",
    auto_convert_enabled: true,
    paypal_usd_auto_convert: true,
  });

  useEffect(() => { api.get(`/gemstones/${id}`).then(({ data }) => setGem(data)); }, [id]);
  // Load global pricing policy once. The two flags it carries are used to
  // (a) hide PayPal when USD auto-convert is disabled and (b) skip the
  // FX conversion on price displays when the merchant wants IDR everywhere.
  useEffect(() => {
    api.get("/pricing/config").then(({ data }) => setPricingConfig(data)).catch(() => {});
  }, []);

  // Fire Meta + TikTok Pixel ViewContent once the gemstone payload arrives.
  // Analytics platforms expect a USD-denominated value for cross-currency
  // ROAS reporting, so we bridge from the gem's base currency before sending.
  useEffect(() => {
    if (gem?.id) {
      const fxRate = rates?.[gem.currency || "USD"] || 1;
      const valueUsd = Number(gem.reserve_price || 0) / fxRate;
      trackViewContent(gem, valueUsd);
      trackTTViewContent(gem, valueUsd);
      ga4ViewItem(gem, valueUsd);
    }
  }, [gem?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve the currently-selected variation (or null) + the effective price/image.
  const selectedVariation = useMemo(() => {
    if (!gem || !variationId) return null;
    return (gem.variations || []).find((v) => v.id === variationId) || null;
  }, [gem, variationId]);

  // Price in the gem's BASE currency (typically IDR going forward, but each
  // product carries its own `currency` field which we honor end-to-end).
  const effectivePriceBase = selectedVariation && selectedVariation.price != null && selectedVariation.price !== ""
    ? Number(selectedVariation.price)
    : Number(gem?.reserve_price || 0);
  const baseCurrency = gem?.currency || "USD";

  // Helper — convert any base-currency amount to USD via FX bridge so that
  // shipping (USD-priced) and the QRIS modal (IDR-priced) get the right
  // inputs irrespective of what the gem's stored base currency is.
  const toUsd = (amount, fromCurrency) => {
    const fromRate = rates?.[fromCurrency || "USD"] || 1;
    return Number(amount || 0) / fromRate;
  };
  const effectivePriceUsd = toUsd(effectivePriceBase, baseCurrency);
  const effectiveImage = selectedVariation?.image_url || null;

  const submitNegotiate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/inquiries", { type: "negotiate", ...form, gemstone_id: id });
      setSent(true);
      toast.success("Concierge will contact you within 24 hours.");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not submit"); }
  };

  const startStripe = async () => {
    if (!checkVariationSelected()) return;
    trackInitiateCheckout(gem, effectivePriceUsd, "stripe");
    trackTTInitiateCheckout(gem, effectivePriceUsd, "stripe");
    ga4BeginCheckout(gem, effectivePriceUsd, "stripe");
    setBusyStripe(true);
    try {
      const { data } = await api.post("/checkout/session", {
        gemstone_id: id, currency, origin_url: window.location.origin,
        variation_id: variationId || undefined,
        shipping_method: shipping.method?.key || undefined,
        shipping_country: shipping.country || undefined,
        ...getMetaAttribution(),
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusyStripe(false);
    }
  };

  const submitBank = async (e) => {
    e.preventDefault();
    if (!checkVariationSelected()) return;
    trackInitiateCheckout(gem, effectivePriceUsd, "bank_transfer");
    trackTTInitiateCheckout(gem, effectivePriceUsd, "bank_transfer");
    ga4BeginCheckout(gem, effectivePriceUsd, "bank_transfer");
    try {
      const { data } = await api.post("/orders/bank-transfer", {
        gemstone_id: id, currency, name: bank.name, email: bank.email, phone: bank.phone, notes: bank.notes,
        variation_id: variationId || undefined,
        shipping_method: shipping.method?.key || undefined,
        shipping_country: shipping.country || undefined,
        ...getMetaAttribution(),
      });
      setBank({ ...bank, result: data });
      toast.success("Wire instructions issued.");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Gate purchase actions on variation selection when variations exist
  const checkVariationSelected = () => {
    if (gem && (gem.variations || []).length > 0 && !variationId) {
      toast.error("Please select a variation first.");
      return false;
    }
    return true;
  };

  if (!gem) return <div className="pt-40 text-center text-white/40">Loading…</div>;

  return (
    <div data-testid="gemstone-detail" className="pt-28">
      <Seo
        title={`${gem.name} · ${gem.carat} ct ${gem.category} · ${gem.origin}`}
        description={`${gem.name} — ${gem.carat} ct ${gem.category} from ${gem.origin}. ${gem.treatment}. ${gem.certification} certified. ${(gem.description || "").slice(0, 100)}`}
        path={`/gemstone/${gem.id}`}
        image={gem.image_url}
        ogType="product"
        jsonLd={[
          productSchema(gem),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Collection", url: "/collection" },
            { name: gem.name },
          ]),
          videoObjectSchema(gem),
        ].filter(Boolean)}
      />
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        <div className="lg:sticky lg:top-28 lg:h-fit">
          <div className="relative">
            {gem.video && gem.video.url && (
              <VideoBadge className="absolute top-3 left-3 z-10" />
            )}
            <MediaGallery
              images={gem.images}
              video={gem.video}
              fallbackImage={effectiveImage || gem.image_url}
              fallbackGallery={gem.gallery}
              alt={gem.name}
            />
          </div>
        </div>

        <div>
          <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-4">{gem.category}</p>
          <h1 className="font-heading font-light text-5xl md:text-6xl leading-[1.05] mb-6">{gem.name}</h1>
          <div className="gold-line w-20 mb-8"></div>
          <p className="text-white/70 leading-relaxed mb-10">{gem.description}</p>

          <div className="grid grid-cols-2 gap-y-6 gap-x-12 mb-12 border-y border-white/10 py-10">
            {[
              ["Weight", `${gem.carat || gem.weight_value || ""} ${gem.weight_unit || "ct"}`.trim()],
              [t("label_origin"), gem.origin],
              ...(gem.dimensions ? [["Dimensions", gem.dimensions]] : []),
              ["Color", gem.color || "—"],
              ["Clarity", gem.clarity || "—"],
              ["Cut", gem.cut || "—"],
              [t("label_treatment"), gem.treatment],
              ["Certification", gem.certification],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2">{k}</div>
                <div className="font-heading text-xl text-ivory">{v}</div>
              </div>
            ))}
          </div>

          <div className="mb-10">
            <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40 mb-2">
              {selectedVariation ? "Variation Price" : t("label_reserve")}
            </div>
            <div className="font-heading text-4xl text-ivory">{format(effectivePriceBase, baseCurrency)}</div>
            <div className="text-white/40 text-xs mt-2">
              Quoted in {currency} · live FX
              {selectedVariation && (
                <span className="text-gold ml-2">· {variationLabel(selectedVariation)}</span>
              )}
            </div>
          </div>

          {/* Variation selector — appears only when the product has variation rows.
              Each row has a numeric quantity stepper so a buyer can build a multi-
              variation cart (e.g. 10× 5mm + 5× 8mm). Tapping any qty on a row also
              updates the legacy single-select `variationId` to keep the inline
              "Buy Now" + bank-transfer single-line flows functional. */}
          {gem.variations && gem.variations.length > 0 && (
            <div data-testid="variations-block" className="mb-6 border border-white/10">
              <div className="px-5 py-3 border-b border-white/10 bg-white/[0.02] flex items-center justify-between">
                <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold">Choose Quantity per Option</span>
                <span className="text-white/40 text-[10px] font-accent tracking-[0.22em] uppercase">{gem.variations.length} option{gem.variations.length === 1 ? "" : "s"}</span>
              </div>
              <ul className="divide-y divide-white/5">
                {gem.variations.map((v) => {
                  const oos = v.stock != null && v.stock <= 0;
                  const priceUsd = v.price != null && v.price !== "" ? Number(v.price) : Number(gem.reserve_price || 0);
                  const qty = variationQty[v.id] || 0;
                  const cap = v.stock != null ? v.stock : Infinity;
                  const setQty = (n) => {
                    const next = Math.max(0, Math.min(cap, Math.floor(Number(n) || 0)));
                    setVariationQty((prev) => ({ ...prev, [v.id]: next }));
                    if (next > 0) setVariationId(v.id);
                    else if (variationId === v.id) setVariationId(null);
                  };
                  return (
                    <li
                      key={v.id}
                      data-testid={`variation-row-${v.id}`}
                      className={`flex items-center justify-between px-5 py-4 ${qty > 0 ? "bg-gold/[0.06]" : ""} ${oos ? "opacity-50" : ""}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className={`font-heading text-lg ${qty > 0 ? "text-gold" : "text-ivory"}`}>{variationLabel(v)}</div>
                        <div className="text-white/40 text-xs mt-0.5">
                          {v.sku && <span>SKU {v.sku}</span>}
                          {v.sku && v.stock != null && <span> · </span>}
                          {v.stock != null && (
                            <span>{oos ? "Out of stock" : `${v.stock} in stock`}</span>
                          )}
                        </div>
                      </div>
                      {/* Qty stepper */}
                      <div className="flex items-center gap-3 mx-4">
                        <button
                          type="button"
                          data-testid={`variation-dec-${v.id}`}
                          onClick={() => setQty(qty - 1)}
                          disabled={qty <= 0 || oos}
                          className="w-7 h-7 border border-white/20 text-white/65 hover:border-gold hover:text-gold transition-colors flex items-center justify-center disabled:opacity-30"
                        >
                          <Minus size={11} />
                        </button>
                        <input
                          type="number"
                          min="0"
                          max={cap}
                          data-testid={`variation-qty-${v.id}`}
                          value={qty}
                          onChange={(e) => setQty(e.target.value)}
                          disabled={oos}
                          className="bg-transparent w-10 text-center text-ivory text-base focus:outline-none border-b border-white/15 focus:border-gold"
                        />
                        <button
                          type="button"
                          data-testid={`variation-inc-${v.id}`}
                          onClick={() => setQty(qty + 1)}
                          disabled={oos || qty >= cap}
                          className="w-7 h-7 border border-white/20 text-white/65 hover:border-gold hover:text-gold transition-colors flex items-center justify-center disabled:opacity-30"
                        >
                          <Plus size={11} />
                        </button>
                      </div>
                      <div className="text-right min-w-[90px]">
                        <div className="font-heading text-lg text-ivory">{format(priceUsd, baseCurrency)}</div>
                        <div className="text-white/40 text-[10px] font-accent tracking-[0.18em] uppercase">{currency}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* SHIPPING — country + method selector. Hidden for digital products. */}
          <ShippingPicker
            subtotalUsd={effectivePriceUsd}
            physical={gem.physical !== false}
            onChange={setShipping}
          />

          {/* ADD TO CART — pulls every row with qty > 0 (or, when no variations
              exist, the base product) into the global cart. If at least one
              row has qty ≥ 1 the button activates; otherwise it nudges the
              buyer to enter a quantity. */}
          {(() => {
            const hasVar = gem.variations && gem.variations.length > 0;
            const pickedRows = hasVar
              ? gem.variations
                  .map((v) => ({ v, qty: variationQty[v.id] || 0 }))
                  .filter((r) => r.qty > 0)
              : [];
            const totalQty = hasVar ? pickedRows.reduce((a, r) => a + r.qty, 0) : 1;
            // Live running total — sum (unit price × qty) for every row the
            // buyer has filled in. Stays in the gem's base currency so the
            // CurrencyContext.format() can convert it on the fly.
            const runningTotalBase = hasVar
              ? pickedRows.reduce((a, r) => {
                  const unit = r.v.price != null && r.v.price !== ""
                    ? Number(r.v.price)
                    : Number(gem.reserve_price || 0);
                  return a + unit * r.qty;
                }, 0)
              : Number(gem.reserve_price || 0);
            const disabled = hasVar && totalQty === 0;

            const handleAddToCart = () => {
              if (hasVar && pickedRows.length === 0) {
                toast.error("Set a quantity on at least one option below.");
                return;
              }
              if (hasVar) {
                pickedRows.forEach(({ v, qty }) => {
                  const unit = v.price != null && v.price !== "" ? Number(v.price) : Number(gem.reserve_price || 0);
                  addItem(
                    {
                      gemstone_id: gem.id,
                      variation_id: v.id,
                      name: gem.name,
                      variation_label: variationLabel(v),
                      image_url: v.image_url || gem.image_url,
                      unit_price: unit,
                      base_currency: gem.currency || "USD",
                      stock: v.stock != null ? Number(v.stock) : null,
                    },
                    qty,
                  );
                });
              } else {
                addItem(
                  {
                    gemstone_id: gem.id,
                    variation_id: null,
                    name: gem.name,
                    variation_label: "",
                    image_url: gem.image_url,
                    unit_price: Number(gem.reserve_price || 0),
                    base_currency: gem.currency || "USD",
                    stock: gem.stock != null ? Number(gem.stock) : null,
                  },
                  1,
                );
              }
              toast.success(`Added ${totalQty} item${totalQty === 1 ? "" : "s"} to cart`);
              setVariationQty({});
              setVariationId(null);
            };

            return (
              <div className="mb-4">
                {/* Shopify-style running total bar — visible only when at least
                    one variation has qty > 0. Updates live as the buyer changes
                    quantities, in the user's display currency. */}
                {hasVar && totalQty > 0 && (
                  <div
                    data-testid="running-total-bar"
                    className="flex items-center justify-between bg-gold/10 border border-gold/40 px-4 py-3 mb-3"
                  >
                    <div>
                      <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-0.5">
                        <TX>Your Selection</TX>
                      </div>
                      <div className="text-white/65 text-xs">
                        {pickedRows.length} <TX>variation{pickedRows.length === 1 ? "" : "s"}</TX> · {totalQty} <TX>item{totalQty === 1 ? "" : "s"}</TX>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/45">
                        <TX>Subtotal</TX>
                      </div>
                      <div data-testid="product-running-total" className="font-heading text-2xl text-gold">
                        {format(runningTotalBase, baseCurrency)}
                      </div>
                    </div>
                  </div>
                )}
                {hasVar && totalQty === 0 && (
                  <div className="text-white/45 text-xs italic text-center py-2 mb-1">
                    <TX>Use the +/− buttons above to set a quantity, then click Add to Cart.</TX>
                  </div>
                )}
                <div className="flex flex-wrap gap-3 items-stretch">
                  <button
                    type="button"
                    data-testid="add-to-cart-btn"
                    onClick={handleAddToCart}
                    disabled={disabled}
                    className={`flex-1 min-w-[200px] flex items-center justify-center gap-2 font-accent uppercase tracking-[0.24em] text-xs px-6 py-4 transition-colors duration-300 ${
                      disabled
                        ? "border border-white/15 text-white/30 cursor-not-allowed"
                        : "bg-gold text-black border border-gold hover:bg-ivory"
                    }`}
                  >
                    <ShoppingBag size={14} />
                    <TX>Add to Cart</TX>
                    {hasVar && totalQty > 0 && (
                      <span className="font-mono text-[10px] normal-case tracking-normal">· {totalQty}</span>
                    )}
                  </button>
                  {cartCount > 0 && (
                    <button
                      type="button"
                      data-testid="view-cart-btn"
                      onClick={() => navigate(lp("/cart"))}
                      className="flex items-center justify-center gap-2 border border-white/20 text-white/75 font-accent uppercase tracking-[0.24em] text-xs px-5 py-4 hover:border-ivory hover:text-ivory transition-colors"
                    >
                      <TX>View Cart</TX>
                      <span className="font-mono text-[10px] text-gold normal-case tracking-normal">· {cartCount}</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* PURCHASE OPTIONS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <button
              data-testid="buy-stripe-btn"
              onClick={startStripe}
              disabled={busyStripe}
              className="flex items-center justify-center gap-2 bg-gold text-black font-accent uppercase tracking-[0.24em] text-xs px-6 py-4 hover:bg-ivory transition-colors duration-500 disabled:opacity-50"
            >
              <CreditCard size={16} /> {busyStripe ? "Redirecting…" : t("cta_buy_secure")}
            </button>
            <button
              data-testid="buy-bank-btn"
              onClick={() => setBank({ ...bank, open: !bank.open })}
              className="flex items-center justify-center gap-2 border border-gold text-gold font-accent uppercase tracking-[0.24em] text-xs px-6 py-4 hover:bg-gold hover:text-black transition-colors duration-500"
            >
              <Building2 size={16} /> {t("cta_bank_transfer")}
            </button>
          </div>

          {/* QRIS — Indonesia-only domestic QR payment rail. Hidden when the
              user's display currency is anything other than IDR, since QRIS
              settles in rupiah and asking the buyer to convert defeats the
              "exact amount" UX the modal is built around. */}
          {currency === "IDR" && (
            <button
              type="button"
              data-testid="buy-qris-btn"
              onClick={() => {
                if (!checkVariationSelected()) return;
                trackInitiateCheckout(gem, effectivePriceUsd, "qris");
                trackTTInitiateCheckout(gem, effectivePriceUsd, "qris");
                ga4BeginCheckout(gem, effectivePriceUsd, "qris");
                setQrisOpen(true);
              }}
              className="w-full flex items-center justify-center gap-2 border border-gold/60 text-ivory font-accent uppercase tracking-[0.24em] text-xs px-6 py-4 hover:bg-gold hover:text-black hover:border-gold transition-colors duration-500 mb-4"
            >
              <QrCode size={16} /> <TX>Pay with QRIS</TX>
              <span className="font-mono text-[10px] text-gold/80 tracking-normal normal-case">· IDR</span>
            </button>
          )}

          {/* PayPal Smart Buttons — rendered only when PayPal is configured server-side
              AND the admin has not turned OFF PayPal USD auto-conversion. */}
          {pricingConfig.paypal_usd_auto_convert && (
            <div data-testid="paypal-wrap" className="mt-6 mb-8">
              <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-white/40 mb-3"><TX>Pay with PayPal</TX></div>
              <PayPalCheckoutButton
                gemstoneId={gem.id}
                variationId={variationId}
                shippingMethod={shipping.method?.key || null}
                shippingCountry={shipping.country || ""}
                onBeforePay={checkVariationSelected}
              />
            </div>
          )}

          {/* External marketplace checkout (Shopee / Tokopedia / TikTok Shop).
              Auto-hidden when no enabled links are configured for this product. */}
          <MarketplaceCheckoutButtons
            gemstoneId={gem.id}
            links={gem.marketplace_links}
          />

          {bank.open && !bank.result && (
            <form onSubmit={submitBank} className="bg-onyx border border-gold/30 p-8 space-y-5 mb-10">
              <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold">Bank Wire Order — {format(effectivePriceBase, baseCurrency)} {currency}</div>
              <input data-testid="bank-name" required placeholder="Full Name" value={bank.name} onChange={(e) => setBank({...bank, name: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
              <input data-testid="bank-email" required type="email" placeholder="Email" value={bank.email} onChange={(e) => setBank({...bank, email: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
              <input data-testid="bank-phone" placeholder="Phone (optional)" value={bank.phone} onChange={(e) => setBank({...bank, phone: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
              <textarea data-testid="bank-notes" placeholder="Notes (optional)" value={bank.notes} onChange={(e) => setBank({...bank, notes: e.target.value})} rows={2} className="bg-transparent border border-white/20 px-3 py-3 w-full focus:outline-none focus:border-gold" />
              <button data-testid="bank-submit" className="bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-7 py-4 hover:bg-ivory">Issue Wire Instructions</button>
            </form>
          )}

          {bank.result && (
            <div data-testid="bank-result" className="bg-onyx border border-gold/40 p-8 mb-10">
              <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-4">Wire Instructions Issued</div>
              <div className="text-ivory font-heading text-3xl mb-2">{Intl.NumberFormat("en-US").format(bank.result.amount)} {bank.result.currency}</div>
              <div className="font-accent text-[10px] tracking-[0.18em] uppercase text-white/50 mb-6">Reference · {bank.result.reference}</div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-white/40">Bank:</span> {bank.result.instructions.bank}</div>
                <div><span className="text-white/40">Account Holder:</span> {bank.result.instructions.account_holder}</div>
                <div><span className="text-white/40">Account Number:</span> {bank.result.instructions.account_number || bank.result.instructions.iban}</div>
                <div><span className="text-white/40">SWIFT:</span> {bank.result.instructions.swift}</div>
              </div>
              <p className="text-white/50 text-xs mt-6">Use the reference number on your wire. Concierge will confirm receipt within 1-2 business days.</p>
            </div>
          )}

          {!sent ? (
            <form onSubmit={submitNegotiate} className="bg-onyx border border-white/10 p-8 space-y-5">
              <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold">{t("cta_negotiate")}</div>
              <input data-testid="neg-name" required placeholder="Full Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
              <input data-testid="neg-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
              <input data-testid="neg-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-transparent border-b border-white/20 px-0 py-3 w-full focus:outline-none focus:border-gold" />
              <textarea data-testid="neg-msg" required placeholder="Your offer or message" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={3} className="bg-transparent border border-white/20 px-3 py-3 w-full focus:outline-none focus:border-gold" />
              <button data-testid="neg-submit" className="bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-7 py-4 hover:bg-ivory transition-colors duration-500">{t("cta_send")}</button>
            </form>
          ) : (
            <div className="bg-onyx border border-gold/40 p-8 text-center">
              <div className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-3">Submitted</div>
              <div className="text-white/70">A senior consultant will contact you within 24 hours.</div>
            </div>
          )}
        </div>
      </section>
      <section className="container mx-auto px-6 lg:px-10">
        <ReviewsSection gemstoneId={gem.id} />
      </section>
      <div className="h-32"></div>
      {/* AI Gemologist — product-aware floating assistant */}
      <GemologistAssistant gemstoneId={gem.id} />
      {/* Mobile-only sticky marketplace bar — auto-hidden when no links configured */}
      <MarketplaceCheckoutButtons
        gemstoneId={gem.id}
        links={gem.marketplace_links}
        sticky
      />
      {/* AI Gemologist — product-aware floating assistant */}
      <GemologistAssistant gemstoneId={gem.id} />
      {/* Mobile-only sticky marketplace bar — auto-hidden when no links configured */}
      <MarketplaceCheckoutButtons
        gemstoneId={gem.id}
        links={gem.marketplace_links}
        sticky
      />
      {/* QRIS Payment Modal (IDR-only). Computes the exact rupiah amount by
          converting the USD subtotal (price + shipping) at the live FX rate
          loaded by CurrencyContext. The buyer is asked to transfer this
          exact amount so reconciliation can be automated by amount-match. */}
      <QrisPaymentModal
        open={qrisOpen}
        onClose={() => setQrisOpen(false)}
        amountIdr={(effectivePriceUsd + (shipping.costUsd || 0)) * (rates?.IDR || 0)}
        gemstoneName={`${gem.name} · ${gem.carat} ct ${gem.category}`}
        gemstoneId={gem.id}
        reference={(gem.id || "").slice(0, 8).toUpperCase()}
        onConfirmPaid={() => setQrisOpen(false)}
      />
    </div>
  );
};

export default GemstoneDetail;
